import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { useLang } from '../i18n/LanguageContext';

export const ImageGallery = ({ media = [], images = [], alt = 'Project image' }) => {
  const { t } = useLang();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const scrollerRef = useRef(null);

  // Support both new format (media array) and old format (images array)
  const mediaItems = media.length > 0
    ? media
    : images.map(src => ({ type: 'image', src }));

  // Detect current slide based on scroll position (native scroll-snap carousel)
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const width = scroller.clientWidth;
        if (width <= 0) return;
        const idx = Math.round(scroller.scrollLeft / width);
        setCurrentIndex((prev) => (prev !== idx ? idx : prev));
      });
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [mediaItems.length]);

  const scrollToIndex = (index) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollTo({
      left: index * scroller.clientWidth,
      behavior: 'smooth',
    });
  };

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? mediaItems.length - 1 : currentIndex - 1;
    scrollToIndex(newIndex);
  };

  const goToNext = () => {
    const newIndex = currentIndex === mediaItems.length - 1 ? 0 : currentIndex + 1;
    scrollToIndex(newIndex);
  };

  const openLightbox = (index) => {
    if (mediaItems[index].type === 'image') {
      setLightboxIndex(index);
      setLightboxOpen(true);
    }
  };

  const closeLightbox = () => setLightboxOpen(false);

  const lightboxPrevious = (e) => {
    if (e) e.stopPropagation();
    setLightboxIndex((prev) => {
      let newIndex = prev - 1;
      while (newIndex >= 0 && mediaItems[newIndex].type !== 'image') newIndex--;
      return newIndex >= 0 ? newIndex : prev;
    });
  };

  const lightboxNext = (e) => {
    if (e) e.stopPropagation();
    setLightboxIndex((prev) => {
      let newIndex = prev + 1;
      while (newIndex < mediaItems.length && mediaItems[newIndex].type !== 'image') newIndex++;
      return newIndex < mediaItems.length ? newIndex : prev;
    });
  };

  if (!mediaItems || mediaItems.length === 0) return null;

  // Single item — no carousel needed
  if (mediaItems.length === 1) {
    const item = mediaItems[0];
    return (
      <div className="relative w-full h-full">
        {item.type === 'video' ? (
          <iframe
            src={item.src}
            title={alt}
            className="w-full h-full"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
            allowFullScreen
          />
        ) : (
          <img
            src={item.src}
            alt={alt}
            className="w-full h-full object-contain bg-muted/5 cursor-pointer"
            onClick={() => openLightbox(0)}
            data-testid="gallery-image-single"
          />
        )}
      </div>
    );
  }

  const currentItem = mediaItems[currentIndex];

  return (
    <>
      <div className="relative w-full h-full group" data-testid="image-gallery">
        {/* Native scroll-snap carousel */}
        <div
          ref={scrollerRef}
          className="relative w-full h-full overflow-x-auto overflow-y-hidden flex snap-x snap-mandatory scroll-smooth"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
            overscrollBehaviorX: 'contain',
          }}
        >
          <style>{`
            [data-testid="image-gallery"] > div::-webkit-scrollbar { display: none; }
          `}</style>
          {mediaItems.map((item, index) => (
            <div
              key={index}
              className="w-full h-full flex-shrink-0 snap-start flex items-center justify-center"
              style={{ minWidth: '100%' }}
            >
              {item.type === 'video' ? (
                <iframe
                  src={item.src}
                  title={`${alt} ${index + 1}`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                  allowFullScreen
                />
              ) : (
                <img
                  src={item.src}
                  alt={`${alt} ${index + 1}`}
                  className="w-full h-full object-contain bg-muted/5 cursor-pointer select-none"
                  onClick={() => openLightbox(index)}
                  draggable={false}
                  data-testid={`gallery-image-${index}`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Navigation Arrows — visible on mobile, hover on desktop */}
        <div className="absolute inset-0 flex items-center justify-between p-2 sm:p-4 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          <Button
            onClick={goToPrevious}
            variant="ghost"
            size="icon"
            className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background/90 text-foreground shadow-md pointer-events-auto"
            aria-label="Anterior"
            data-testid="gallery-prev-btn"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Button>

          <Button
            onClick={goToNext}
            variant="ghost"
            size="icon"
            className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background/90 text-foreground shadow-md pointer-events-auto"
            aria-label="Siguiente"
            data-testid="gallery-next-btn"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Button>
        </div>

        {/* Dot Indicators */}
        <div className="absolute bottom-3 sm:bottom-4 left-0 right-0 flex justify-center gap-2 pointer-events-none">
          {mediaItems.map((item, index) => (
            <button
              key={index}
              onClick={() => scrollToIndex(index)}
              className={`transition-all duration-300 pointer-events-auto ${
                index === currentIndex
                  ? 'bg-accent w-6 h-2'
                  : 'bg-background/60 hover:bg-background/80 w-2 h-2'
              } rounded-full`}
              aria-label={`Ir a ${item.type === 'video' ? 'video' : 'imagen'} ${index + 1}`}
              data-testid={`gallery-dot-${index}`}
            />
          ))}
        </div>

        {/* Media Counter */}
        <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-background/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-foreground flex items-center gap-2 pointer-events-none">
          {currentItem.type === 'video' && (
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
          {currentIndex + 1} / {mediaItems.length}
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
            onClick={closeLightbox}
            aria-label="Cerrar"
            data-testid="lightbox-close-btn"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="relative w-full h-full flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={mediaItems[lightboxIndex].src}
              alt={`${alt} ${lightboxIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              style={{ touchAction: 'pinch-zoom' }}
            />

            {lightboxIndex > 0 && (
              <button
                onClick={lightboxPrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white flex items-center justify-center"
                aria-label="Anterior"
                data-testid="lightbox-prev-btn"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {lightboxIndex < mediaItems.length - 1 && (
              <button
                onClick={lightboxNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white flex items-center justify-center"
                aria-label="Siguiente"
                data-testid="lightbox-next-btn"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full text-white text-sm">
              {lightboxIndex + 1} / {mediaItems.filter(item => item.type === 'image').length} {t.projects.counterImages}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
